import { ContentCopy, Link, OpenInNew } from "@mui/icons-material";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { MouseEvent as ReactMouseEvent } from "react";
import { useTranslation } from "react-i18next";

interface ContextMenuProps {
  anchorPosition: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  onOpenInNewTab: () => void;
  url: string;
  contextMenuEvent?: ReactMouseEvent;
}

const ContextMenu = ({
  anchorPosition,
  open,
  onClose,
  onOpenInNewTab,
  url,
  contextMenuEvent,
}: ContextMenuProps) => {
  const { t } = useTranslation("common");

  const getTextToCopy = (): { text: string; disabled: boolean } => {
    // Check if there's selected text
    const selectedText = window.getSelection()?.toString() || "";

    if (selectedText) {
      return { text: selectedText, disabled: false };
    }

    // No selected text, copy all text from the cell
    if (contextMenuEvent) {
      const target = contextMenuEvent.target as HTMLElement;
      const cell = target.closest("td");
      const cellText = cell?.textContent?.trim() || "";

      return {
        text: cellText,
        disabled: !cellText,
      };
    }

    return { text: "", disabled: true };
  };

  const copyData = getTextToCopy();

  const handleCopyText = async () => {
    if (copyData.disabled) return;

    try {
      await navigator.clipboard.writeText(copyData.text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = copyData.text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    onClose();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    onClose();
  };

  const menuItemStyle = {
    padding: "6px 8px",
    minHeight: 32,
    fontWeight: 400,
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  };

  const iconStyle = {
    minWidth: 24,
  };

  const paperStyle = {
    minWidth: 240,
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    padding: 4,
    backgroundColor: "#ffffff",
  };

  return (
    <Menu
      open={open}
      onClose={onClose}
      autoFocus={false}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          style: paperStyle,
        },
      }}
      MenuListProps={{
        style: {
          padding: 0,
        },
      }}
    >
      <MenuItem onClick={onOpenInNewTab} sx={menuItemStyle}>
        <ListItemIcon style={iconStyle}>
          <OpenInNew style={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: "14px" }}>
          {t("openInNewTab")}
        </ListItemText>
      </MenuItem>

      <MenuItem onClick={handleCopyLink} sx={menuItemStyle}>
        <ListItemIcon style={iconStyle}>
          <Link style={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: "14px" }}>
          {t("copyLink")}
        </ListItemText>
      </MenuItem>

      <MenuItem
        onClick={handleCopyText}
        disabled={copyData.disabled}
        sx={{
          ...menuItemStyle,
          opacity: copyData.disabled ? 0.5 : 1,
          "&:hover": {
            backgroundColor: copyData.disabled ? "transparent" : "#f5f5f5",
          },
        }}
      >
        <ListItemIcon
          style={{
            ...iconStyle,
            opacity: copyData.disabled ? 0.5 : 1,
          }}
        >
          <ContentCopy style={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: "14px" }}>
          {t("copyText")}
        </ListItemText>
      </MenuItem>
    </Menu>
  );
};

export default ContextMenu;
